import { SelectOption } from '@components/selectmulti';
import {
  ExtendedFieldMappingEditorValue,
  FieldMappingEditorValue,
  FieldType,
} from '@components/text-editors/addons/field-mapping';

import {
  FieldConfigDraft,
  FieldConfigDraftEntry,
  FieldConfigValue,
  FieldSource,
  JourneyAttribute,
  ResolvedFieldEntry,
  StoredFieldConfig,
  StoredFieldEntry,
} from './types';

export type BaseFieldKey = 'name' | 'email' | 'phone';

/**
 * The load-bearing parts of each base-field mapping — the variable token,
 * source, field, and type the backend's convert_fieldmapped_value resolves
 * when a traveller is added to a journey. This is the single source of truth
 * useBaseFieldDefaults (translated pill labels, UI-facing) builds from, via
 * buildBaseFieldDefault below.
 */
export const BASE_FIELD_TOKENS: Record<
  BaseFieldKey,
  { token: string; source: string; field: string; type: FieldType }
> = {
  name: { token: 'application_name', source: 'application', field: 'name', type: 'text' },
  email: { token: 'application_email', source: 'application', field: 'email', type: 'email' },
  phone: { token: 'application_phone', source: 'application', field: 'phone', type: 'phone' },
};

/**
 * The draft is keyed by a composite key, not a bare short_id: a journey
 * attribute and a company-traveller attribute are Hashids of different
 * upstream models, so the same string can legitimately name one of each, and a
 * shared key would let configuring one silently configure the other.
 *
 * `entry.source` + `entry.shortId` are what the serializers write back with —
 * the composite key never leaves the draft.
 */
export const TRAVELLER_KEY_PREFIX = 'traveller__';

export const draftKey = (attr: Pick<JourneyAttribute, 'shortId' | 'source'>): string =>
  attr.source === 'traveller' ? `${TRAVELLER_KEY_PREFIX}${attr.shortId}` : attr.shortId;

export const travellerDraftKey = (shortId: string): string => `${TRAVELLER_KEY_PREFIX}${shortId}`;

export const storedSectionFor = (source: FieldSource | undefined): 'fields' | 'traveller_fields' =>
  source === 'traveller' ? 'traveller_fields' : 'fields';

/** Draft-key -> resolved entry, across both catalogs. */
export const baselineByDraftKey = (
  resolvedFields: ResolvedFieldEntry[],
  resolvedTravellerFields: ResolvedFieldEntry[],
): Map<string, ResolvedFieldEntry> => {
  const map = new Map<string, ResolvedFieldEntry>();
  for (const entry of resolvedFields) {
    map.set(entry.short_id, entry);
  }
  for (const entry of resolvedTravellerFields) {
    map.set(travellerDraftKey(entry.short_id), entry);
  }
  return map;
};

/** Build a base-field FieldMappingEditorValue from its canonical token + a (possibly translated) label. */
export const buildBaseFieldDefault = (key: BaseFieldKey, label: string) => {
  const { token, source, field, type } = BASE_FIELD_TOKENS[key];
  return {
    value: `{{ ${token} }}`,
    variables: { [token]: { source, field, label, type } },
  };
};

/**
 * Coerce a stored field value into the FieldMappingEditorValue the editors
 * expect: a dict passes through, a plain string is wrapped with empty
 * variables, and everything else (array / boolean / undefined) has no editor
 * representation.
 */
export const toEditorValue = (
  value: FieldConfigValue | undefined,
): FieldMappingEditorValue | undefined => {
  if (value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
    return value as FieldMappingEditorValue;
  }
  if (typeof value === 'string') {
    return { value, variables: {} };
  }
  return undefined;
};

/**
 * Whether a stored value would pre-fill its field at add-time (mirrors the
 * backend's _is_prefilled): non-empty string, deliberate boolean, non-empty
 * list, or a FieldMappingEditorValue dict with a non-empty inner value.
 */
export const hasFillValue = (value: FieldConfigValue | undefined): boolean => {
  if (value === undefined) {
    return false;
  }
  if (typeof value === 'string') {
    return value.trim() !== '';
  }
  if (typeof value === 'boolean') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  return typeof value.value === 'string' && value.value.trim() !== '';
};

/**
 * Map a stored multi-select value (an array of option short_ids) to the
 * SelectOption[] the SelectMulti/select-list components render.
 *
 * A stored id whose option has since left the list still renders — with
 * `unavailableLabel` standing in for its label — so it round-trips on save
 * instead of silently vanishing. Non-array values (including undefined) and
 * non-string entries produce no selection. This is the same rule the config
 * panel and the send sidebar both apply to a stored multi-select value.
 */
export const toSelectedOptions = ({
  value,
  options,
  unavailableLabel,
}: {
  value: FieldConfigValue | undefined;
  options: SelectOption[];
  unavailableLabel: string;
}): SelectOption[] => {
  const items = (Array.isArray(value) ? value : []) as Array<
    string | ExtendedFieldMappingEditorValue
  >;
  const storedIds = items.filter((item): item is string => typeof item === 'string');
  const optionByValue = new Map(options.map((option) => [option.value, option]));
  return storedIds.map(
    (storedId) => optionByValue.get(storedId) ?? { value: storedId, label: unavailableLabel },
  );
};

/**
 * Runtime guard for the FieldMappingEditorValue invariant: every entry in a
 * `variables` map must be a FieldMappingFieldItem object — the backend's
 * convert_fieldmapped_value calls .get("type") on it and crashes on bare
 * strings when the journey send runs. The FieldMappingTextEditor only ever produces dicts,
 * so a violation here means a programming error upstream of serialization.
 */
function assertNoBareStringVariables(value: FieldConfigValue | undefined): void {
  if (value === undefined || typeof value !== 'object' || Array.isArray(value)) {
    return;
  }
  const variables = (value as FieldMappingEditorValue).variables ?? {};
  for (const [token, variableValue] of Object.entries(variables)) {
    if (typeof variableValue !== 'object' || variableValue === null) {
      throw new Error(
        `field_config variables entry "${token}" must be a FieldMappingFieldItem object, got ${typeof variableValue}`,
      );
    }
  }
}

/**
 * Build an in-memory draft from a stored field_config + live attribute list.
 *
 * Rules:
 * - A field row exists only when the stored entry opted it in (included =
 *   true); journey-required attributes no longer force rows — the Journeys
 *   API does not enforce them, so skipping them is fine.
 * - isRequired (the Hire-side "marked required" flag) comes from the stored
 *   entry's `required` key; configs saved before the key existed fall back to
 *   the attribute's own isRequired.
 * - value: taken from the stored entry if present, else undefined.
 * - Attributes present in attrs but missing from stored config get default state.
 * - The ordering of draft.fields matches the live attrs list (AC-7).
 */
export function buildDraftFromFieldConfig(
  storedConfig: Partial<StoredFieldConfig>,
  attrs: JourneyAttribute[],
): FieldConfigDraft {
  // A journey that has never been configured comes back as field_config: {}
  // — both `fields` and `base` are absent, not empty objects.
  const storedFields = storedConfig.fields ?? {};
  const storedBase = storedConfig.base ?? {};
  const fields: Record<string, FieldConfigDraftEntry> = {};

  const storedTravellerFields = storedConfig.traveller_fields ?? {};

  for (const attr of attrs) {
    const source: FieldSource = attr.source ?? 'journey';
    const stored =
      source === 'traveller' ? storedTravellerFields[attr.shortId] : storedFields[attr.shortId];

    const entry: FieldConfigDraftEntry = {
      source,
      shortId: attr.shortId,
      included: stored?.included ?? false,
      isRequired: stored?.required ?? attr.isRequired ?? false,
      title: attr.title,
      attributeType: attr.attributeType,
      options: attr.options ?? null,
      value: stored?.value,
    };
    fields[draftKey(attr)] = entry;
  }

  const base: FieldConfigDraft['base'] = {
    name: storedBase.name?.value,
    email: storedBase.email?.value,
    phone: storedBase.phone?.value,
  };

  return { base, fields, startJourney: storedConfig.start_journey ?? true };
}

/**
 * Convert an in-memory draft back to the StoredFieldConfig shape for persisting.
 *
 * Rules:
 * - Only fields with included = true are emitted in the fields map: a field
 *   exists in the config iff the admin added it — journey-required
 *   attributes are never auto-included.
 * - Each emitted entry carries the Hire-side `required` flag from the draft.
 * - Field keys are the raw attribute short_ids, emitted byte-for-byte — the
 *   config endpoint stores them verbatim, so mixed-case Hashids survive.
 * - FieldMappingEditorValue dicts are preserved as-is; a runtime assertion
 *   rejects any variables entry that is not an object.
 */
export function buildFieldConfigFromDraft(
  draft: FieldConfigDraft,
  storedConfig: Partial<StoredFieldConfig> = {},
): StoredFieldConfig {
  const fields: StoredFieldConfig['fields'] = {};
  const travellerFields: Record<string, StoredFieldEntry> = {};

  for (const [key, entry] of Object.entries(draft.fields)) {
    if (!entry.included) {
      continue;
    }
    assertNoBareStringVariables(entry.value);
    const stored: StoredFieldEntry = {
      included: true,
      required: entry.isRequired,
      value: entry.value ?? '',
    };
    const shortId = entry.shortId ?? key;
    if (storedSectionFor(entry.source) === 'traveller_fields') {
      travellerFields[shortId] = stored;
    } else {
      fields[shortId] = stored;
    }
  }

  const base: StoredFieldConfig['base'] = {};
  if (draft.base.name !== undefined) {
    assertNoBareStringVariables(draft.base.name);
    base.name = { value: draft.base.name };
  }
  if (draft.base.email !== undefined) {
    assertNoBareStringVariables(draft.base.email);
    base.email = { value: draft.base.email };
  }
  if (draft.base.phone !== undefined) {
    assertNoBareStringVariables(draft.base.phone);
    base.phone = { value: draft.base.phone };
  }

  // Carry over any stored entry the draft did not represent at all.
  //
  // This rebuilds the whole blob from the draft, and the draft only holds
  // entries for attributes it was built with — the traveller catalog is a
  // separate query, so a draft seeded before it arrives represents none of
  // them. Emitting only what the draft knows about would then delete the
  // admin's stored traveller configuration on the next save. A field the
  // draft *does* represent and the admin opted out is still dropped: that is
  // a removal, not an absence.
  const represented: Record<'fields' | 'traveller_fields', Set<string>> = {
    fields: new Set(),
    traveller_fields: new Set(),
  };
  for (const [key, entry] of Object.entries(draft.fields)) {
    represented[storedSectionFor(entry.source)].add(entry.shortId ?? key);
  }
  const preserve = (
    section: 'fields' | 'traveller_fields',
    target: Record<string, StoredFieldEntry>,
  ) => {
    for (const [shortId, entry] of Object.entries(storedConfig[section] ?? {})) {
      if (!represented[section].has(shortId)) {
        target[shortId] = entry;
      }
    }
  };
  preserve('fields', fields);
  preserve('traveller_fields', travellerFields);

  // Only emit the traveller section when it has entries: an empty map and an
  // absent key read the same downstream, and writing one on every save would
  // add a key to configs that never used traveller fields.
  return {
    base,
    fields,
    ...(Object.keys(travellerFields).length > 0 ? { traveller_fields: travellerFields } : {}),
    start_journey: draft.startJourney,
  };
}

/**
 * Merge a partial draft update into the current draft immutably.
 * Used by the sidebar to apply individual field changes without
 * replacing the whole draft.
 */
export function mergeDraftFieldUpdate(
  draft: FieldConfigDraft,
  shortId: string,
  update: Partial<FieldConfigDraftEntry>,
): FieldConfigDraft {
  const existingEntry = draft.fields[shortId];
  if (!existingEntry) {
    return draft;
  }
  return {
    ...draft,
    fields: {
      ...draft.fields,
      [shortId]: { ...existingEntry, ...update },
    },
  };
}

/**
 * Add attributes to the draft (opt-in via the add-fields modal). Any journey
 * attribute is addable — journey-required and optional alike.
 *
 * Re-adding a field that is still present in the draft (removeFieldFromDraft
 * only flips included=false, it never clears the entry) restores the state
 * it had before removal — both the value and the Hire-side isRequired flag —
 * because remove/re-add within the same session is an undo, not a reset. A
 * field with no existing draft entry still seeds isRequired from the
 * attribute's own isRequired.
 */
export function addFieldsToDraft(
  draft: FieldConfigDraft,
  shortIds: string[],
  attrs: JourneyAttribute[],
): FieldConfigDraft {
  const attrMap = new Map(attrs.map((attr) => [draftKey(attr), attr]));
  const updatedFields = { ...draft.fields };

  for (const key of shortIds) {
    const attr = attrMap.get(key);
    if (!attr) {
      continue;
    }
    const existingEntry = updatedFields[key];
    updatedFields[key] = {
      source: attr.source ?? 'journey',
      shortId: attr.shortId,
      included: true,
      isRequired: existingEntry?.isRequired ?? attr.isRequired,
      title: attr.title,
      attributeType: attr.attributeType,
      options: attr.options ?? null,
      value: existingEntry?.value ?? (attr.attributeType === 'boolean' ? false : undefined),
    };
  }

  return { ...draft, fields: updatedFields };
}

/**
 * Remove an added field from the draft (opt it out).
 *
 * Only `included` flips to false — the value (and the isRequired flag) is
 * left exactly as it was. Removing only opts the field out; keeping the
 * value lets addFieldsToDraft restore it if the field is re-added in the
 * same session. No serializer can leak the retained value:
 * buildFieldConfigFromDraft omits excluded entries entirely, and
 * buildJobOverrideFromDraft diffs the retained value against the same
 * baseline it was read from, so opting a field out never invents a value
 * override.
 */
export function removeFieldFromDraft(draft: FieldConfigDraft, shortId: string): FieldConfigDraft {
  const entry = draft.fields[shortId];
  if (!entry) {
    return draft;
  }
  return {
    ...draft,
    fields: {
      ...draft.fields,
      [shortId]: { ...entry, included: false },
    },
  };
}

/**
 * Seed the canonical base-field mappings the global config panel exposes
 * (Full name / Email) when they are absent — so a save without touching them
 * still persists the applicant-variable mappings rather than an empty base.
 *
 * Phone is deliberately not seeded: the global panel no longer surfaces it, so
 * a fresh config never writes one. An already-stored base.phone is preserved
 * untouched (data safety for configs saved before the panel dropped phone).
 */
export function seedBaseDefaults(
  draft: FieldConfigDraft,
  defaults: { name: FieldMappingEditorValue; email: FieldMappingEditorValue },
): FieldConfigDraft {
  return {
    ...draft,
    base: {
      ...draft.base,
      name: draft.base.name ?? defaults.name,
      email: draft.base.email ?? defaults.email,
    },
  };
}

/**
 * The visible base fields carrying no value, in `visibleBaseKeys` order.
 *
 * Base fields are labelled "Always required" because a traveller is created
 * from them — an empty name or email would reach the Journeys API as blank. The
 * editors let their mapping pill be deleted, so the panel checks this before
 * enabling Save; clearing one and saving used to persist an empty base that
 * seedBaseDefaults could no longer repair (the key exists, so it is not
 * re-seeded).
 */
export function findEmptyBaseFields(
  draft: FieldConfigDraft,
  visibleBaseKeys: readonly BaseFieldKey[],
): BaseFieldKey[] {
  return visibleBaseKeys.filter((key) => !hasFillValue(draft.base[key]));
}

/** Field-mapping counts shown in the config panel's journey card. */
export type DraftCounts = {
  mapped: number;
  totalIncluded: number;
};

/**
 * Summarize the draft's field-mapping state, for the config panel's journey
 * card: "{fieldsCount} fields · {preFilledCount} pre-filled", matching every
 * other surface.
 *
 * - mapped: visible base fields carrying a fill value + included attribute
 *   entries carrying a fill value.
 * - totalIncluded: visible base fields + included attribute entries.
 */
export function summarizeDraftCounts(
  draft: FieldConfigDraft,
  visibleBaseKeys: readonly BaseFieldKey[],
): DraftCounts {
  const includedEntries = Object.values(draft.fields).filter((entry) => entry.included);
  const numBaseFields = visibleBaseKeys.length;
  const mappedBase = visibleBaseKeys.filter((key) => hasFillValue(draft.base[key])).length;

  return {
    mapped: mappedBase + includedEntries.filter((entry) => hasFillValue(entry.value)).length,
    totalIncluded: numBaseFields + includedEntries.length,
  };
}

/**
 * Build an in-memory draft from the job config endpoint's `resolved_fields`
 * array.
 *
 * The resolved fields already encode the merged global + per-job state
 * (included / required / value all resolved job > global), so the per-job
 * panel opens pre-filled with the inherited configuration.
 *
 * Rules:
 * - Field ordering follows the live attrs list; resolved entries without a
 *   live attribute are ignored (stale-config grace) and live attributes
 *   without a resolved entry get default state.
 * - included / isRequired come straight from the resolved entry — no
 *   journey-required forcing (the Journeys API does not enforce it).
 * - value is taken from the resolved entry only when a source resolved it
 *   (value_source 'job' or 'global'); otherwise the draft value stays
 *   undefined so an untouched empty field never diffs against the baseline.
 * - Base fields are not part of the resolved-fields payload — the per-job
 *   panel edits attribute fields only; base mappings stay on the global
 *   config.
 */
export function buildDraftFromResolvedFields(
  resolvedFields: ResolvedFieldEntry[],
  attrs: JourneyAttribute[],
  resolvedTravellerFields: ResolvedFieldEntry[] = [],
  startJourney = true,
): FieldConfigDraft {
  const baselines = baselineByDraftKey(resolvedFields, resolvedTravellerFields);
  const fields: Record<string, FieldConfigDraftEntry> = {};

  for (const attr of attrs) {
    const key = draftKey(attr);
    const resolved = baselines.get(key);
    fields[key] = {
      source: attr.source ?? 'journey',
      shortId: attr.shortId,
      included: resolved?.included ?? false,
      isRequired: resolved?.required ?? attr.isRequired ?? false,
      title: attr.title,
      attributeType: attr.attributeType,
      options: attr.options ?? null,
      value: resolved && resolved.value_source !== null ? resolved.value : undefined,
    };
  }

  return { base: {}, fields, startJourney };
}

/** Structural equality over FieldConfigValue (literals, arrays, FieldMappingEditorValue dicts). */
const isEqualFieldValue = (left: unknown, right: unknown): boolean => {
  if (left === right) {
    return true;
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return (
      left.length === right.length &&
      left.every((item, index) => isEqualFieldValue(item, right[index]))
    );
  }
  if (
    left !== null &&
    right !== null &&
    typeof left === 'object' &&
    typeof right === 'object' &&
    !Array.isArray(left) &&
    !Array.isArray(right)
  ) {
    const leftRecord = left as Record<string, unknown>;
    const rightRecord = right as Record<string, unknown>;
    const leftKeys = Object.keys(leftRecord);
    return (
      leftKeys.length === Object.keys(rightRecord).length &&
      leftKeys.every((key) => isEqualFieldValue(leftRecord[key], rightRecord[key]))
    );
  }
  return false;
};

/**
 * Diff a per-job draft against the resolved-fields baseline and produce the
 * per-job override to PUT — only the keys this job overrides (the
 * field_config for JobJourneyConfig).
 *
 * Only short_ids the user actually changed relative to the resolved
 * (inherited) state are written:
 * - included is pinned only when it differs from the resolved included flag;
 * - required is pinned only when it differs from the resolved required flag;
 * - value is pinned only when it structurally differs from the resolved value;
 * - untouched short_ids keep whatever the existing job config already stored
 *   (overrides that happen to currently match the global value must survive
 *   later global edits), and inherited global values are never copied into
 *   the job row.
 *
 * `resetShortIds` (from the per-job panel's "reset to global" action) short-
 * circuits the diff: a reset short_id is dropped from the stored fields map
 * entirely, before any comparison against the draft — that is what hands the
 * field back to global inheritance, and it must win over whatever
 * existingJobConfig already held for that key.
 */
export function buildJobOverrideFromDraft(
  draft: FieldConfigDraft,
  resolvedFields: ResolvedFieldEntry[],
  existingJobConfig: Partial<StoredFieldConfig>,
  resetShortIds: readonly string[] = [],
  resolvedTravellerFields: ResolvedFieldEntry[] = [],
  globalStartJourney?: boolean,
): StoredFieldConfig {
  const baselines = baselineByDraftKey(resolvedFields, resolvedTravellerFields);
  const resetSet = new Set(resetShortIds);
  const sections: Record<'fields' | 'traveller_fields', Record<string, StoredFieldEntry>> = {
    fields: { ...(existingJobConfig.fields ?? {}) },
    traveller_fields: { ...(existingJobConfig.traveller_fields ?? {}) },
  };

  for (const [key, entry] of Object.entries(draft.fields)) {
    const section = sections[storedSectionFor(entry.source)];
    const shortId = entry.shortId ?? key;
    if (resetSet.has(key)) {
      delete section[shortId];
      continue;
    }
    const baseline = baselines.get(key);
    const baselineIncluded = baseline?.included ?? false;
    const baselineRequired = baseline?.required ?? false;
    const baselineValue = baseline?.value ?? '';
    const draftValue = entry.value ?? '';

    const override: StoredFieldEntry = { ...(section[shortId] ?? {}) };
    let isChanged = false;
    if (entry.included !== baselineIncluded) {
      override.included = entry.included;
      isChanged = true;
    }
    if (entry.isRequired !== baselineRequired) {
      override.required = entry.isRequired;
      isChanged = true;
    }
    if (!isEqualFieldValue(draftValue, baselineValue)) {
      assertNoBareStringVariables(draftValue);
      override.value = draftValue;
      isChanged = true;
    }
    if (isChanged) {
      section[shortId] = override;
    }
  }

  return {
    base: { ...(existingJobConfig.base ?? {}) },
    fields: sections.fields,
    // Same rule as buildFieldConfigFromDraft: no empty section on a config that
    // never used one.
    ...(Object.keys(sections.traveller_fields).length > 0
      ? { traveller_fields: sections.traveller_fields }
      : {}),
    ...resolveStartJourneyOverride(draft, existingJobConfig, globalStartJourney),
  };
}

/**
 * The job's start_journey key, present only while it disagrees with the global
 * value — toggling back to what global says is how it is reset, so this key
 * needs no entry in resetShortIds.
 *
 * Without globalStartJourney the caller does not edit this key, so an existing
 * one is carried through untouched rather than dropped.
 */
function resolveStartJourneyOverride(
  draft: FieldConfigDraft,
  existingJobConfig: Partial<StoredFieldConfig>,
  globalStartJourney?: boolean,
): { start_journey?: boolean } {
  if (globalStartJourney === undefined) {
    return existingJobConfig.start_journey === undefined
      ? {}
      : { start_journey: existingJobConfig.start_journey };
  }
  return draft.startJourney === globalStartJourney ? {} : { start_journey: draft.startJourney };
}

/**
 * Hand a field back to global inheritance: restore its included / isRequired
 * / value from the entry's global side of the resolved-fields baseline.
 * `value` becomes undefined when the global config never stored a value (as
 * opposed to storing an empty one) — has_global_value is what distinguishes
 * the two. title / attributeType / options are display metadata and are left
 * as-is. A no-op when the short_id has no draft entry.
 */
export function resetFieldToGlobal(
  draft: FieldConfigDraft,
  shortId: string,
  baseline: ResolvedFieldEntry,
): FieldConfigDraft {
  const entry = draft.fields[shortId];
  if (!entry) {
    return draft;
  }
  return {
    ...draft,
    fields: {
      ...draft.fields,
      [shortId]: {
        ...entry,
        included: baseline.global_included,
        isRequired: baseline.global_required,
        value: baseline.has_global_value ? baseline.global_value : undefined,
      },
    },
  };
}

/**
 * Whether a draft entry's per-job state diverges from the global side of the
 * resolved-fields baseline — drives the "Overridden" badge in the per-job
 * panel. This compares live, against the draft as the user edits it, not only
 * the persisted job override, so the badge tracks a change the moment it is
 * made. Always false when there is no baseline (e.g. a stale attribute no
 * longer in the resolved-fields payload).
 */
export function isFieldOverriddenFromGlobal(
  entry: FieldConfigDraftEntry,
  baseline: ResolvedFieldEntry | undefined,
): boolean {
  if (!baseline) {
    return false;
  }
  return (
    entry.included !== baseline.global_included ||
    entry.isRequired !== baseline.global_required ||
    !isEqualFieldValue(
      entry.value ?? '',
      baseline.has_global_value ? (baseline.global_value ?? '') : '',
    )
  );
}

/**
 * Structural equality between two drafts, used to derive the per-job panel's
 * real dirty state (a draft edited back to its loaded baseline is not
 * dirty). Compares startJourney, the three base entries, and every fields
 * entry's included / isRequired / value — title / attributeType / options
 * are display metadata copied from the live attribute list and are ignored.
 */
export function areDraftsEqual(left: FieldConfigDraft, right: FieldConfigDraft): boolean {
  if (left.startJourney !== right.startJourney) {
    return false;
  }
  const baseKeys: BaseFieldKey[] = ['name', 'email', 'phone'];
  if (!baseKeys.every((key) => isEqualFieldValue(left.base[key] ?? '', right.base[key] ?? ''))) {
    return false;
  }

  const leftShortIds = Object.keys(left.fields);
  if (leftShortIds.length !== Object.keys(right.fields).length) {
    return false;
  }
  return leftShortIds.every((shortId) => {
    const leftEntry = left.fields[shortId];
    const rightEntry = right.fields[shortId];
    return (
      rightEntry !== undefined &&
      leftEntry.included === rightEntry.included &&
      leftEntry.isRequired === rightEntry.isRequired &&
      isEqualFieldValue(leftEntry.value ?? '', rightEntry.value ?? '')
    );
  });
}
